import { Dialog } from "@mui/material";
import { X, Check, Edit } from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useState, useEffect } from "react";
import API from "../API/newIndex";
import Notification from "../Component/Notification";
import { Transition } from "./Transition";
import { GenerateSlugName } from "./GenerateSlugName";
import { InputComponent, SelectComponent, ButtonComponent } from "@shopeasy-dashboard-ui/ui-library";

const MetaFieldForm = ({ openMetaFieldForm, handleClose, heading, editData = {}, onSuccess }) => {
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const [regexError, setRegexError] = useState("");
  const [isEditingSlug, setIsEditingSlug] = useState(false);
  const [tempSlug, setTempSlug] = useState("");
  const [isSlugManual, setIsSlugManual] = useState(false);

  const isEditMode = Object.keys(editData).length > 0;

  const [formData, setFormData] = useState({
    field_name: editData?.custom_field_name ?? "",
    field_name_space: editData?.custom_field_slug ?? editData?.custom_field_name_space ?? "",
    // field_description: editData?.custom_field_description ?? '',
    field_type: editData?.custom_field_type ?? "",
    validation_regex: editData?.validation_regex ?? "",
    validation_error_message: editData?.validation_error_message ?? "",
    is_required: editData?.is_required ?? false,
  });

  useEffect(() => {
    if (editData && Object.keys(editData).length > 0) {
      const initialSlug = editData?.custom_field_slug ?? editData?.custom_field_name_space ?? "";
      setFormData({
        field_name: editData?.custom_field_name ?? "",
        field_name_space: initialSlug,
        field_type: editData?.custom_field_type ?? "",
        validation_regex: editData?.validation_regex ?? "",
        validation_error_message: editData?.validation_error_message ?? "",
        is_required: editData?.is_required ?? false,
      });
      setTempSlug(initialSlug);
      setIsSlugManual(true);
    } else {
      setIsSlugManual(false);
    }
    setIsEditingSlug(false);
  }, [editData, openMetaFieldForm]);

  useEffect(() => {
    if (!isEditMode && !isSlugManual) {
      const generateAndSetSlug = async () => {
        const slug = await GenerateSlugName(formData.field_name);
        setFormData((prev) => ({ ...prev, field_name_space: slug }));
      };
      generateAndSetSlug();
    }
  }, [formData.field_name, isEditMode, isSlugManual]);

  const handleStartEditSlug = () => {
    if (isEditMode) return;
    setTempSlug(formData.field_name_space);
    setIsEditingSlug(true);
  };

  const handleSaveSlug = async () => {
    const slug = await GenerateSlugName(formData.field_name_space || "");
    setFormData((prev) => ({ ...prev, field_name_space: slug }));
    setIsSlugManual(true);
    setIsEditingSlug(false);
  };

  const handleCancelSlug = () => {
    setFormData((prev) => ({ ...prev, field_name_space: tempSlug }));
    setIsEditingSlug(false);
  };
  const baseOptions = [
    "text",
    "textarea",
    "number",
    "email",
    "url",
    "tel",
    "date",
    "date-time",
    "time",
    "toggle",
    "image",
    "video",
    "color",
    "richtext",
    "tag",
  ];
  const extraTypeMap = {
    Registration: ["file", "number"],
    Checkout: ["number"],
    Manufacture: ["number"],
  };

  const options = [...baseOptions, ...(extraTypeMap[heading] || [])];

  const handleCloseNotification = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  const isValidRegex = (value) => {
    try {
      new RegExp(value);
      return true;
    } catch {
      return false;
    }
  };

  const buttonDisabled = formData.field_name === "" || formData.field_name_space === "" || formData.field_type === "";

  const handleInput = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateBeforeSubmit = () => {
    if (heading === "Registration" && formData.validation_regex) {
      if (!isValidRegex(formData.validation_regex)) {
        setRegexError("Invalid regex pattern");
        return false;
      }
    }
    setRegexError("");
    return true;
  };

  const handleForm = async (e) => {
    e.preventDefault();
    if (!validateBeforeSubmit()) return;

    try {
      if (Object.keys(editData).length > 0) {
        const payload = {
          custom_field_name: formData.field_name,
          custom_field_name_space: formData.field_name_space,
          custom_field_slug: formData.field_name_space,
          custom_field_type: formData.field_type,
          is_required: Boolean(formData.is_required),
          ...(heading === "Registration" && {
            validation_regex: formData.validation_regex,
            validation_error_message: formData.validation_error_message,
          }),
        };
        const ApiCall = await API({
          url: `/admin/metafieldgroup/UpdateMetaField/${editData?._id}`,
          method: "put",
          data: payload,
        });
        if (ApiCall.status === 200 || ApiCall.status === 304) {
          setIsNotification(true);
          setNotificationMsg("Meta Field Updated Successfully");
          setSeverity("success");
          if (onSuccess) onSuccess();
          handleClose();
        }
      } else {
        const payload = {
          type: heading?.toLowerCase(),
          custom_fields: {
            custom_field_name: formData.field_name,
            custom_field_name_space: formData.field_name_space,
            custom_field_slug: formData.field_name_space,
            custom_field_type: formData.field_type,
            is_required: Boolean(formData.is_required),
            ...(heading === "Registration" && {
              validation_regex: formData.validation_regex,
              validation_error_message: formData.validation_error_message,
            }),
          },
        };

        const ApiCall = await API({
          url: "/admin/metafieldgroup/AddMetaField",
          method: "post",
          data: payload,
        });

        if (ApiCall.status === 200 || ApiCall.status === 304) {
          setIsNotification(true);
          setNotificationMsg("Meta Field Created Successfully");
          setSeverity("success");
          if (onSuccess) onSuccess();
          setTimeout(() => {
            handleClose();
          }, 500);
        }
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    }
  };
  return (
    <>
      {isNotification && notificationMsg && (
        <Notification
          message={notificationMsg}
          close={handleCloseNotification}
          severity={severity}
        />
      )}
      <Dialog
        open={openMetaFieldForm}
        TransitionComponent={Transition}
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description customer-popup"
      >
        <div className="secondary-bg-color w-auto sm:w-[400px] md:w-[500px] lg:w-[600px] xl:w-[600px] max-h-full border main-border-color space-y-4 rounded-[6px] p-5">
          <div className="flex border-b text-primary main-border-color pb-3 items-center justify-between">
            <h6 className="font-medium text-xl">
              {Object.keys(editData).length > 0 ? "Edit" : "Add"} {heading} Meta Field
            </h6>
            <button type="button" className="close-btn" onClick={handleClose}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onKeyDown={(e) => e.key === "Enter" && e.preventDefault()} className="w-full space-y-4">
            <div>
              <InputComponent
                id="field_name"
                name="field_name"
                label={t("lang_Field_Name")}
                required
                autoFocus
                value={formData.field_name}
                onChange={handleInput}
                placeholder="e.g. Tech"
              />
              <div className="flex items-center space-x-1 text-[13px] mt-1.5 min-h-[24px]">
                {!isEditingSlug ? (
                  <>
                    <span
                      className={`text-[#3b82f6] font-medium select-none ${!isEditMode ? "hover:underline cursor-pointer" : ""
                        }`}
                      onClick={!isEditMode ? handleStartEditSlug : undefined}
                    >
                      Key
                    </span>
                    <div className="flex items-center space-x-1 text-gray-500">
                      <span>custom_fields.{formData.field_name_space || ""}</span>
                      {!isEditMode && (
                        <button
                          type="button"
                          onClick={handleStartEditSlug}
                          className="text-gray-400 hover:text-gray-600 transition duration-200 p-0.5"
                          title="Edit key slug"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center space-x-1 text-gray-500 w-full">
                    <span className="select-none font-medium text-[#3b82f6]">Key</span>
                    <span className="select-none">custom_fields.</span>
                    <InputComponent
                      id="field_name_space"
                      name="field_name_space"
                      value={formData.field_name_space}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          field_name_space: e.target.value,
                        }))
                      }
                      onKeyDown={async (e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          await handleSaveSlug();
                        } else if (e.key === "Escape") {
                          handleCancelSlug();
                        }
                      }}
                      placeholder="e.g. tech"
                      required
                      size="sm"
                      className="!h-6 !py-0 !px-2 text-[13px] w-48"
                      isSlug={true}
                    />
                    <button
                      type="button"
                      onClick={handleSaveSlug}
                      className="text-green-600 hover:text-green-800 transition duration-200 p-1"
                      title="Save"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <SelectComponent
                id="field_type"
                name="field_type"
                label="Field Type"
                required
                options={options}
                value={formData.field_type}
                onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
                placeholder="Select Field Type"
              />
            </div>

            {heading === "Registration" && (
              <>
                <div>
                  <InputComponent
                    id="validation_regex"
                    name="validation_regex"
                    label="Validation Regex"
                    value={formData.validation_regex}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        validation_regex: e.target.value,
                      })
                    }
                    placeholder="^[0-9A-Z]{15}$"
                    error={regexError}
                  />
                </div>
                <div>
                  <InputComponent
                    id="validation_error_message"
                    name="validation_error_message"
                    label="Validation Error Message"
                    value={formData.validation_error_message}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        validation_error_message: e.target.value,
                      })
                    }
                    placeholder="Please enter a valid value"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm mb-1 font-medium text-[var(--primary-text-color)]">Required</label>
              <div
                className="flex items-center gap-3 cursor-pointer w-fit"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    is_required: !prev.is_required,
                  }))
                }
              >
                <div
                  className={`form-toggle-track ${formData.is_required ? "active" : ""}`}
                >
                  <div className="form-toggle-thumb" />
                </div>
              </div>
            </div>

            <ButtonComponent
              type="button"
              variant="primary"
              disabled={buttonDisabled}
              onClick={handleForm}
              className="w-full mt-4"
            >
              {Object.keys(editData).length > 0 ? "Edit" : "Add"} {t("lang_Meta_Field")}
            </ButtonComponent>
          </form>
        </div>
      </Dialog>
    </>
  );
};

export default MetaFieldForm;
